import React, { useMemo, useState } from "react";
import { difference, union } from "lodash-es";
import ClassNames from "classnames";
import CheckBox from "../checkBox/CheckBox";
import TextInput from "../TextInput/TextInput";
import Loader from "../../components/Loader/Loader";
import Tooltip from "../../components/Tooltip/Tooltip";
import { useIcon } from "../../assets/useIcon";
import type {
  SelectableListOption,
  SelectableListValue,
} from "./SelectableListTypes";
import "./SelectableList.scss";

interface SelectableListProps {
  options?: SelectableListOption[];
  value?: SelectableListValue[];
  onChange: (value: SelectableListValue[]) => void;
  disabled?: boolean;
  loading?: boolean;
  searchable?: boolean;
  searchPlaceholder?: string;
  emptyState?: React.ReactNode;
  toolbar?: React.ReactNode;
  trailingColumnLabel?: React.ReactNode;
  className?: string;
  "data-testid"?: string;
}

const DEFAULT_SEARCH_PLACEHOLDER = "Type to search...";
const NO_RESULTS_SUBTITLE = "No results";

const matchesSearch = (option: SelectableListOption, search: string): boolean =>
  typeof option.label !== "string" ||
  option.label.toLowerCase().includes(search.toLowerCase());

const getHeaderLabel = (selectedCount: number, totalCount: number): string =>
  selectedCount > 0
    ? `${selectedCount} of ${totalCount} selected`
    : `Select All (${totalCount})`;

const SelectableList: React.FC<SelectableListProps> = ({
  options,
  value,
  onChange,
  disabled = false,
  loading = false,
  searchable = false,
  searchPlaceholder = DEFAULT_SEARCH_PLACEHOLDER,
  emptyState = null,
  toolbar,
  trailingColumnLabel,
  className,
  "data-testid": dataTestId = "",
}) => {
  const [searchValue, setSearchValue] = useState("");
  const { getSvg } = useIcon();
  const testIdSuffix = dataTestId ? `-${dataTestId}` : "";

  const displayedOptions = useMemo(
    () =>
      searchable && searchValue
        ? (options ?? []).filter((option) => matchesSearch(option, searchValue))
        : (options ?? []),
    [options, searchable, searchValue]
  );

  const isOptionDisabled = (option: SelectableListOption): boolean =>
    disabled || !!option.disabled;

  const isOptionSelected = (option: SelectableListOption): boolean =>
    !!value?.includes(option.value);

  const selectableOptions = displayedOptions.filter(
    (option) => !isOptionDisabled(option)
  );
  const selectedDisplayedCount =
    displayedOptions.filter(isOptionSelected).length;
  const selectedSelectableCount =
    selectableOptions.filter(isOptionSelected).length;
  const isAllSelected =
    selectableOptions.length > 0 &&
    selectedSelectableCount === selectableOptions.length;
  const isPartiallySelected = selectedSelectableCount > 0 && !isAllSelected;

  const handleSelectAll = (
    _selectAllValue: SelectableListValue,
    isChecked: boolean
  ) => {
    const selectableValues = selectableOptions.map((option) => option.value);
    const currentValue = value ?? [];

    onChange(
      isChecked
        ? union(currentValue, selectableValues)
        : difference(currentValue, selectableValues)
    );
  };

  const handleRowChange = (
    option: SelectableListOption,
    isChecked: boolean
  ) => {
    if (isOptionDisabled(option)) {
      return;
    }

    const currentValue = value ?? [];

    onChange(
      isChecked
        ? union(currentValue, [option.value])
        : difference(currentValue, [option.value])
    );
  };

  const renderBody = () => {
    if (loading) {
      return (
        <Loader
          center
          className="is-spinner"
          img={getSvg("icon-spinner")}
          fullHeight
          iconSize={"xxxxl"}
        />
      );
    }

    if (!displayedOptions.length) {
      return (
        emptyState ?? (
          <Loader
            className={"no-results"}
            flexcenter
            noIconMargin
            fullHeight
            iconSize={"xxl"}
            icon={"seem-icon-filters-no-saved"}
            subtitle={NO_RESULTS_SUBTITLE}
          />
        )
      );
    }

    return displayedOptions.map((option) => {
      const isRowDisabled = isOptionDisabled(option);
      const isRowSelected = isOptionSelected(option);

      return (
        <React.Fragment key={String(option.value)}>
          <div
            className={ClassNames("selectable-list-row", {
              "is-selected": isRowSelected,
              "is-disabled": isRowDisabled,
            })}
            data-testid={`selectable-list-row-${option.value}${testIdSuffix}`}
          >
            <Tooltip
              type="info"
              content={isRowDisabled ? option.disabledTooltip : undefined}
            >
              <div
                className={"selectable-list-label"}
                data-testid={`selectable-list-label-${option.value}${testIdSuffix}`}
              >
                <CheckBox
                  data-testid={`${option.value}${testIdSuffix}`}
                  wrapperClassName={"selectable-list-checkbox"}
                  label={option.label}
                  icon={option.icon}
                  value={option.value}
                  checked={isRowSelected}
                  disabled={isRowDisabled}
                  searchValue={searchValue}
                  onChange={(_cbValue, isChecked) =>
                    handleRowChange(option, isChecked)
                  }
                />
              </div>
            </Tooltip>
            {option.trailing !== undefined && (
              <div className={"selectable-list-trailing"}>
                {option.trailing}
              </div>
            )}
          </div>
        </React.Fragment>
      );
    });
  };

  return (
    <div
      className={ClassNames("selectable-list", className)}
      data-testid={dataTestId ? `${dataTestId}-selectable-list` : undefined}
    >
      {(!!toolbar || searchable) && (
        <div className={"selectable-list-toolbar"}>
          {toolbar}
          {searchable && (
            <TextInput
              wrapperClassName={"selectable-list-search"}
              data-testid={`selectable-list-search${testIdSuffix}`}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setSearchValue(e.target.value)
              }
              placeholder={searchPlaceholder}
              isClearable
              leftIcon={"seem-icon-search"}
              value={searchValue}
              noMargin
            />
          )}
        </div>
      )}
      <div className={"selectable-list-container"}>
        <div className={"selectable-list-header"}>
          <CheckBox
            data-testid={`select-all${testIdSuffix}`}
            wrapperClassName={"selectable-list-checkbox"}
            label={getHeaderLabel(
              selectedDisplayedCount,
              displayedOptions.length
            )}
            checked={isAllSelected}
            indeterminate={isPartiallySelected}
            disabled={!selectableOptions.length}
            onChange={handleSelectAll}
          />
          {!!trailingColumnLabel && (
            <span className={"selectable-list-column-header"}>
              {trailingColumnLabel}
            </span>
          )}
        </div>
        <div className={"selectable-list-body"}>{renderBody()}</div>
      </div>
    </div>
  );
};

export default SelectableList;
